/* ==========================================================================
   BRITTEN CUSTOM: Britten footer -- behaviour (one small vanilla module)
   ==========================================================================

   Added:       October 2026
   Author:      Claude for Matthew
   Location:    assets/britten-footer.js
   Loaded by:   sections/britten-footer.liquid (<script defer>, at the end of
                the section)

   PURPOSE
   Everything the new footer does that CSS cannot: the mobile accordions, the
   workshop's open / closed status, the Bath time text, the visitor's own time
   line and the "Shipping to" dropdown. No jQuery, no globals.

   HOW IT WORKS
   - Markup is server-rendered (sections/britten-footer.liquid and the
     snippets/britten-footer-*.liquid snippets); the script only fills in and
     toggles. Every customer-visible string reaches the script as a data-bf-s-*
     attribute on the workshop card (translated server-side), so this file holds
     no English.
   - Accordions: the three nav buttons are server-rendered disabled. Below 768px
     the script enables them and the click toggles the panel's hidden attribute
     and the button's aria-expanded (the chevron turns by CSS). From 768px up the
     buttons stay disabled (and lose aria-expanded) so they are plain headings; the
     panels show by CSS. Everything starts collapsed. A media query listener keeps
     this right when the window is resized.
   - Workshop: OPEN = 09:00 to 16:00 Europe/London, Monday to Friday, and not on
     a closed date. The closed dates are in data-bf-closed on the card (one
     YYYY-MM-DD per line, from the section setting). Recomputed once a minute,
     on the minute, and again when the tab comes back into view. It sets
     data-bf-state (open or closed) on .bf-status, the status label and the link
     text. Until it runs the CSS shows the link alone.
   - Shutdown: when the card carries data-bf-shutdown-from and -to (UTC instants)
     and now falls between them, the workshop is closed whatever the hour, the
     label is data-bf-s-shutdown and the reply line ([data-bf-reply]) is
     data-bf-s-reply-shutdown. Outside the window the server's reply line is
     put back. Missing attributes mean no shutdown.
   - Time text: "9:43am", lowercase, no space; on the hour the minutes are dropped
     ("9am"). The weekday is the full name, in Bath.
   - Visitor line: shown only when the visitor's own formatted time differs from
     Bath's, as "It's 4:43am where you are in New York." The city is the last
     part of the visitor's IANA time zone name, with underscores turned to
     spaces. No line when there is no usable city (UTC and the Etc/ zones).
   - Shipping to: choosing a country submits the localization form, or, when
     window.BrittenLocPref is active, opens that popover pre-selected to the
     chosen country instead (the same behaviour the classic footer had inline).
   - Idempotent init(); runs again on shopify:section:load (theme editor).

   THEME DEPENDENCY
   - sections/britten-footer.liquid + snippets/britten-footer-*.liquid (data-bf hooks)
   - The clock itself is assets/britten-bath-clock.js (a separate element)
   - snippets/britten-location-preferences.liquid (window.BrittenLocPref, optional)

   CHANGELOG
   - 19 October 2026: Shutdown window (autumn shutdown 20-27 Oct 2026).
   - October 2026: Initial version (footer v2, handoff turn 5).
   ========================================================================== */

(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false; // true -> logs what init() found and did

  var TZ = 'Europe/London';
  var OPEN_FROM = 9 * 60; // 09:00
  var OPEN_TO = 16 * 60; // 16:00 (closed from then on)
  var DESKTOP_QUERY = '(min-width: 768px)';

  // === HELPER FUNCTIONS ===
  function log(message) {
    if (DEBUG_MODE && window.console) console.log('[Britten Footer] ' + message);
  }

  var timer = null; // the minute timer, one per page
  var refresh = null; // the current footer's update(), for the visibility listener
  var bathFmt = null;
  try {
    bathFmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    });
  } catch (e) {
    bathFmt = null;
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  // Bath's wall clock right now, as plain numbers: y, mo, d, h, mi, weekday
  // (the full English name) and dow (0 = Sunday to 6 = Saturday).
  function bathNow() {
    if (!bathFmt) return null;
    var p = {};
    var parts;
    try {
      parts = bathFmt.formatToParts(new Date());
    } catch (e) {
      return null;
    }
    for (var i = 0; i < parts.length; i++) p[parts[i].type] = parts[i].value;
    var y = +p.year;
    var mo = +p.month;
    var d = +p.day;
    return {
      y: y,
      mo: mo,
      d: d,
      h: (+p.hour) % 24,
      mi: +p.minute,
      weekday: p.weekday,
      dow: new Date(Date.UTC(y, mo - 1, d)).getUTCDay(),
      ymd: y + '-' + pad(mo) + '-' + pad(d)
    };
  }

  // "9:43am", or "9am" on the hour: lowercase, no space.
  function clockText(h, mi) {
    var hh = h % 12 || 12;
    return hh + (mi ? ':' + pad(mi) : '') + (h < 12 ? 'am' : 'pm');
  }

  function visitorCity() {
    try {
      var tz = new Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (tz.indexOf('/') === -1 || tz.indexOf('Etc/') === 0) return '';
      return tz.split('/').pop().replace(/_/g, ' ');
    } catch (e) {
      return '';
    }
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  // === MAIN LOGIC ===
  function initAccordions(root) {
    var mq = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
    var buttons = root.querySelectorAll('[data-bf-acc]');
    var pairs = [];

    Array.prototype.forEach.call(buttons, function (btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      pairs.push({ btn: btn, panel: panel });
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
      });
    });

    function sync() {
      var desktop = mq ? mq.matches : true;
      pairs.forEach(function (pair) {
        pair.btn.disabled = desktop;
        if (desktop) {
          pair.btn.removeAttribute('aria-expanded');
        } else {
          pair.btn.setAttribute('aria-expanded', pair.panel.hidden ? 'false' : 'true');
        }
      });
    }

    sync();
    if (mq) {
      if (mq.addEventListener) mq.addEventListener('change', sync);
      else if (mq.addListener) mq.addListener(sync);
    }
    log('accordions: ' + pairs.length);
  }

  function initCountry(root) {
    var select = root.querySelector('[data-bf-country]');
    if (!select) return;
    select.addEventListener('change', function () {
      // The classic footer did this inline. When the shared location popover is
      // active it opens pre-selected to this country, so the shopper can also
      // pick a language; until then window.BrittenLocPref does not exist and the
      // form submits straight away.
      if (window.BrittenLocPref && window.BrittenLocPref.active) {
        window.BrittenLocPref.open(select.value);
      } else if (select.form) {
        select.form.submit();
      }
    });
  }

  function initWorkshop(root) {
    var card = root.querySelector('[data-bf-card]');
    if (!card) return;

    var status = card.querySelector('[data-bf-status]');
    var label = card.querySelector('[data-bf-status-label]');
    var link = card.querySelector('[data-bf-status-link]');
    var timeEl = card.querySelector('[data-bf-time]');
    var dayEl = card.querySelector('[data-bf-weekday]');
    var local = card.querySelector('[data-bf-local]');
    var reply = card.querySelector('[data-bf-reply]');

    var sOpen = card.getAttribute('data-bf-s-open') || '';
    var sClosed = card.getAttribute('data-bf-s-closed') || '';
    var sMsgOpen = card.getAttribute('data-bf-s-msg-open') || '';
    var sMsgClosed = card.getAttribute('data-bf-s-msg-closed') || '';
    var sLocal = card.getAttribute('data-bf-s-local') || '';
    var sShutdown = card.getAttribute('data-bf-s-shutdown') || '';
    var sReplyShutdown = card.getAttribute('data-bf-s-reply-shutdown') || '';
    var sReply = reply ? reply.textContent : '';
    var shutdownFrom = Date.parse(card.getAttribute('data-bf-shutdown-from') || '');
    var shutdownTo = Date.parse(card.getAttribute('data-bf-shutdown-to') || '');
    var closedDates = card.getAttribute('data-bf-closed') || '';
    var closedList = closedDates.match(/\d{4}-\d{2}-\d{2}/g) || [];
    var city = visitorCity();

    function update() {
      var now = bathNow();
      if (!now) {
        log('no Intl time zone support: status left neutral');
        return;
      }
      var minutes = now.h * 60 + now.mi;
      var nowMs = Date.now();
      var shutdown = !isNaN(shutdownFrom) && !isNaN(shutdownTo) && nowMs >= shutdownFrom && nowMs < shutdownTo;
      var isOpen = !shutdown && now.dow >= 1 && now.dow <= 5 && minutes >= OPEN_FROM && minutes < OPEN_TO && closedList.indexOf(now.ymd) === -1;

      if (status) status.setAttribute('data-bf-state', isOpen ? 'open' : 'closed');
      setText(label, isOpen ? sOpen : (shutdown && sShutdown ? sShutdown : sClosed));
      setText(link, isOpen ? sMsgOpen : sMsgClosed);
      setText(reply, shutdown && sReplyShutdown ? sReplyShutdown : sReply);

      var bathText = clockText(now.h, now.mi);
      setText(timeEl, bathText);
      setText(dayEl, now.weekday);

      if (local) {
        var visitor = new Date();
        var visitorText = clockText(visitor.getHours(), visitor.getMinutes());
        if (city && sLocal && visitorText !== bathText) {
          setText(local, sLocal.replace(/\{\{\s*time\s*\}\}/g, visitorText).replace(/\{\{\s*city\s*\}\}/g, city));
          local.hidden = false;
        } else {
          local.hidden = true;
        }
      }
      log('bath ' + now.ymd + ' ' + bathText + ' ' + (isOpen ? 'open' : shutdown ? 'shutdown' : 'closed'));
    }

    // Run now, then on every minute boundary (plus a beat, so the new minute has begun).
    function schedule() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        update();
        schedule();
      }, 60000 - (Date.now() % 60000) + 50);
    }

    refresh = update;
    update();
    schedule();
  }

  function init() {
    if (!FEATURE_ENABLED) return;
    var root = document.querySelector('[data-bf]');
    if (!root) {
      log('no footer on this page');
      return;
    }
    if (root.getAttribute('data-bf-ready') === '1') {
      log('already initialised');
      return;
    }
    root.setAttribute('data-bf-ready', '1');
    initAccordions(root);
    initCountry(root);
    initWorkshop(root);
    log('ready');
  }

  init();
  // The theme editor swaps the section's markup when a setting changes.
  document.addEventListener('shopify:section:load', init);
  // A tab that was in the background may have missed several minutes.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && refresh) refresh();
  });
})();
